import {
  ChangeEvent,
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Box } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import SaveAlt from '@material-ui/icons/SaveAlt';
import { GlobalContext } from 'globalContext/GlobalContext';
import moment from 'moment';
import {
  apiUrl,
  businessTripByClientUrl,
  businessTripByProjectUrl,
  businessTripByUserUrl,
  businessTripReportsUrl,
  employeesUrl,
  reportsUrl,
  workTimeByClientUrl,
  workTimeByProjectUrl,
  workTimeByUserUrl,
} from 'router/url';
import ContainedButton from 'components/Button/ContainedButton';
import Datepicker from 'components/Datepicker';
import MenuBar from 'components/MenuBar';
import SwitchContainer from 'components/SwitchContainer';
import Table from 'components/Table/Table';
import { Report, Sorting, TableHead } from 'components/Table/types';
import TimesheetFilter from 'components/TimesheetFilter/TimesheetChipsFilter';
import { ContractTypes } from 'modules/BusinessTrips/Dialog/types';
import { fetchWithAuthorization } from 'utils/authorizedFetch';
import { fetchClientList, fetchProjectList } from 'utils/commonFetches';
import {
  dateFormatBackend,
  dateFormatFrontend,
  formatDate,
  formatMonthsNumbersToStringNames,
} from 'utils/dateFormats';
import {
  getEncodedQueryParamsString,
  QueryParameters,
} from 'utils/encoding/encodeQueryParamsString';
import { getFetch } from 'utils/fetchFunctions';
import { returnLastMonthDays } from 'utils/helpers';
import {
  filterDropdownProjectData,
  getRenamedProjectsData,
  setClientsValue,
} from 'utils/helpers/filterDropdownData';
import {
  ClientsData,
  FilterOption,
  ProjectsData,
} from 'utils/helpers/renameKeys';
import { usePagination } from 'utils/hooks/usePagination';
import { useToggleState } from 'utils/hooks/useToggleState';
import {
  BUSINESS_TRIPS_HEAD_DATA,
  TAB_LABELS,
  TIMEREPORTS_HEAD_DATA,
} from './static_data';
import {
  EnovaFormDataProps,
  FetchingUrlArgument,
  FilterComponent,
  Filtering,
  FormDataProps,
  ReportsFilter,
} from './types';
import { GridWrapperWithMargin, GridWrapperWithMargin2 } from './Reports.css';

// "Name Surname", as elsewhere, in the Employee filter and the export file
// name (change request of 24.10, B6).
const joinNames = (object: {
  username: string;
  name: string;
  surname: string;
}) => {
  return {
    value: object.username,
    name: `${object.name} ${object.surname}`,
  };
};

const Reports = () => {
  const [tableDataEmpty, setTableDataEmpty] = useState(false);
  const [employeesData, setEmployeesData] = useState<FilterOption[]>([]);
  const [projectsData, setProjectsData] = useState<ProjectsData[]>([]);
  const [tableHeadData, setTableHeadData] = useState<TableHead[]>([]);
  const [clientsData, setClientsData] = useState<ClientsData[]>([]);
  const [showWorkTimeInDays, toggleWorktime] = useToggleState(true);

  const [filteredProjectsData, setFilteredProjectsData] = useState<
    ProjectsData[]
  >([]);
  const [tableData, setTableData] = useState<Report[]>([]);
  // ENOVA filter state is managed by react-hook-form below

  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState(0);
  const [sorting, setSorting] = useState<Sorting>({
    column: 'surname',
    direction: 'asc',
  });
  const [showMultiplicators, setShowMultiplicators] = useState(false);

  const handleSwitchChange = () =>
    setShowMultiplicators((prevState: boolean) => !prevState);

  const { firstDay, lastDay } = useMemo(() => returnLastMonthDays(), []);

  const {
    localization: { currentLocale },
  } = useContext(GlobalContext);

  const { control, setValue } = useForm<FormDataProps & EnovaFormDataProps>({
    defaultValues: {
      project: { projectName: undefined },
      client: { clientName: undefined },
      name: { value: null, name: null },
      dateFrom: firstDay,
      dateTo: lastDay,
      year: { value: null, name: null },
      month: { value: null, name: null },
      contractType: { value: null, name: null },
    },
  });

  const formData = useWatch({ control }) as FormDataProps & EnovaFormDataProps;
  const { name, client, project, dateFrom, dateTo, year, month, contractType } =
    formData;
  const {
    debouncedPage,
    pagination,
    setRowsPerPage,
    setCurrentPage,
    setElementsCount,
  } = usePagination<FormDataProps>({
    initialPage: 0,
    initialRowsPerPage: 10,
    initialElementsCount: 10,
    debounceTime: 500,
    data: formData,
  });

  const intl = useIntl();

  const clearProjectAfterClearingClient = useCallback(() => {
    setValue('project', { projectName: undefined });
  }, [setValue]);

  const projectOptions = useMemo(
    () => getRenamedProjectsData(filteredProjectsData),
    [filteredProjectsData],
  );

  const REPORTS_FILTER_DATA = [
    {
      label: 'TABLE_FILTER.LABEL.CLIENT',
      type: Filtering.client,
      options: clientsData,
      component: FilterComponent.selectFilter,
      handleChange: clearProjectAfterClearingClient,
    },
    {
      label: 'TABLE_FILTER.LABEL.PROJECT',
      type: Filtering.project,
      options: projectOptions,
      component: FilterComponent.selectFilter,
    },
    {
      label: 'TABLE_FILTER.LABEL.NAME',
      type: Filtering.name,
      options: employeesData,
      component: FilterComponent.selectFilter,
    },
    {
      label: 'TABLE_FILTER.LABEL.DATE_FROM',
      type: Filtering.dateFrom,
      component: FilterComponent.datepicker,
    },
    {
      label: 'TABLE_FILTER.LABEL.DATE_TO',
      type: Filtering.dateTo,
      component: FilterComponent.datepicker,
    },
  ];

  // ENOVA Export filters data
  const currentYear = new Date().getFullYear();
  const yearsOptions = useMemo(
    () =>
      Array.from({ length: currentYear - 2018 + 1 }, (_v, i) => 2018 + i).map(
        (y) => ({ value: y, name: String(y) }),
      ),
    [currentYear],
  );
  const monthsOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_v, i) => i + 1).map((m) => ({
        value: m,
        name: String(m),
      })),
    [],
  );

  const contractTypeOptions = useMemo(() => {
    return [
      {
        value: ContractTypes.B2B,
        name: intl.formatMessage({ id: 'BUSINESS_TRIP.B2B' }),
      },
      {
        value: ContractTypes.CIVIL_CONTRACT,
        name: intl.formatMessage({ id: 'BUSINESS_TRIP.CIVIL_CONTRACT' }),
      },
      {
        value: ContractTypes.EMPLOYMENT_CONTRACT,
        name: intl.formatMessage({ id: 'BUSINESS_TRIP.EMPLOYMENT_CONTRACT' }),
      },
    ];
  }, [intl]);

  const ENOVA_FILTER_DATA: ReportsFilter[] = [
    {
      label: 'TABLE_FILTER.LABEL.YEAR',
      type: Filtering.year,
      options: yearsOptions as any,
      component: FilterComponent.selectFilter,
    },
    {
      label: 'TABLE_FILTER.LABEL.MONTH',
      type: Filtering.month,
      options: monthsOptions as any,
      component: FilterComponent.selectFilter,
    },
    {
      label: 'TABLE_FILTER.LABEL.EMPLOYMENT_TYPE',
      type: Filtering.contractType,
      options: contractTypeOptions as any,
      component: FilterComponent.selectFilter,
    },
  ];

  const fetchingUrlsObject = ((viewType: number) => {
    let type: 'timereports' | 'business_trips' | 'enova_export' =
      'enova_export';
    if (viewType === 0) {
      type = 'timereports';
    } else if (viewType === 1) {
      type = 'business_trips';
    }
    const timeReportsUrls = {
      reports: reportsUrl,
      employees: workTimeByUserUrl,
      clients: workTimeByClientUrl,
      projects: workTimeByProjectUrl,
    };

    const businessTripsReportsUrls = {
      reports: businessTripReportsUrl,
      employees: businessTripByUserUrl,
      clients: businessTripByClientUrl,
      projects: businessTripByProjectUrl,
    };

    const enovaExportUrls = {
      reports: reportsUrl, // not used for list; only download endpoint below
      employees: '',
      clients: '',
      projects: '',
    };

    const switchObject = {
      timereports: timeReportsUrls,
      business_trips: businessTripsReportsUrls,
      enova_export: enovaExportUrls,
    };

    return switchObject[type];
  })(viewMode);

  const returnQueryParams = (): QueryParameters => {
    if (viewMode === 2) {
      return {
        month: `${month?.value ?? ''}`,
        year: `${year?.value ?? ''}`,
        contractType: `${contractType?.value ?? ''}`,
      } as QueryParameters;
    }
    return {
      page: `${pagination.pageNumber || ''}`,
      size: `${pagination.pageSize || ''}`,
      sort: `${sorting.column + ',' + sorting.direction}`,
      dateFrom: `${
        dateFrom ? `${formatDate(dateFrom, dateFormatBackend)}` : ''
      }`,
      dateTo: `${dateTo ? `${formatDate(dateTo, dateFormatBackend)}` : ''}`,
      username: `${name?.value ? `${name.value}` : ''}`,
      client: `${client?.clientName ? `${client.clientName}` : ''}`,
      project: `${project?.projectName ? `${project.projectName}` : ''}`,
      showMultipliers: `${showMultiplicators}`,
      showWorkTimeInDays: `${showWorkTimeInDays}`,
    } as QueryParameters;
  };

  const returnCurrentFetchingUrl = ({
    queryParams,
    typeOfFile,
  }: FetchingUrlArgument): string => {
    if (viewMode === 2) {
      // ENOVA export dedicated endpoint
      return (
        '/api/work-time/timesheet/xlsx' +
        getEncodedQueryParamsString(queryParams)
      );
    }
    return (
      fetchingUrlsObject.reports +
      (typeOfFile ? '/' + typeOfFile : '') +
      getEncodedQueryParamsString(queryParams)
    );
  };

  const returnFileName = () => {
    const [formattedDateFrom, formattedDateTo] = [dateFrom, dateTo].map(
      (date) => formatDate(date, dateFormatFrontend),
    );
    let mode = 'Enova export';
    if (viewMode === 0) {
      mode = 'Timereports';
    } else if (viewMode === 1) {
      mode = 'Trips';
    }

    const returnFileNameDate =
      `${dateFrom ? ` from ${formattedDateFrom}` : ''}` +
      `${dateTo ? ` to ${formattedDateTo}` : ''}`;

    const returnFileNameFilter =
      `${name?.name ? ` employee-${name.name}` : ''}` +
      `${client?.clientName ? ` client-${client.clientName}` : ''}` +
      `${project?.projectName ? ` project-${project.projectName}` : ''}`;

    if (viewMode === 2) {
      const suffix = `.xlsx`;
      const enovaFilter =
        `${year?.value ? ` year-${year.value}` : ''}` +
        `${month?.value ? ` month-${month.value}` : ''}` +
        `${contractType?.value ? ` contract-${contractType.value}` : ''}`;
      return `${mode}${enovaFilter || ''}${suffix}`;
    }
    if (returnFileNameFilter) {
      return `${mode}${returnFileNameDate}${returnFileNameFilter}${
        viewMode ? `.csv` : `.xlsx`
      }`;
    }
    return `General ${mode}${returnFileNameDate}${viewMode ? `.csv` : `.xlsx`}`;
  };

  const exportFile = (response: BlobPart, fileTitle: string) => {
    const url = window.URL.createObjectURL(new Blob([response]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileTitle);
    document.body.appendChild(link);
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const download = (filename: string, text: any) => {
    const pom = document.createElement('a');
    pom.setAttribute(
      'href',
      'data:text/csv;charset=utf-8,' + encodeURIComponent(text),
    );
    pom.setAttribute('download', filename);
    if (document.createEvent) {
      const event = document.createEvent('MouseEvents');
      event.initEvent('click', true, true);
      pom.dispatchEvent(event);
    } else {
      pom.click();
    }
  };

  const getReport = async () => {
    const typeOfFile = viewMode === 1 ? 'csv' : 'xlsx';

    const downloadUrl = returnCurrentFetchingUrl({
      queryParams: returnQueryParams(),
      typeOfFile,
    });
    setLoading(true);

    try {
      const response = await fetchWithAuthorization(apiUrl + downloadUrl, {
        headers: { Accept: '*/*' },
      });
      if (!response.ok) {
        throw new Error();
      }
      const fileName = returnFileName();
      if (viewMode === 1) {
        download(fileName, await response.text());
      } else {
        exportFile(await response.blob(), fileName);
      }
    } catch {
      toast.error(intl.formatMessage({ id: 'TOAST_UNDEFINED_ERROR' }));
    } finally {
      setLoading(false);
    }
  };

  const renderDownloadButton = () => {
    if (viewMode === 0 || viewMode === 2) {
      const enovaDisabled =
        viewMode === 2 &&
        (!year?.value || !month?.value || !contractType?.value);
      return (
        <ContainedButton
          id="downloadExcelButton"
          disabled={
            viewMode === 2
              ? enovaDisabled || loading
              : !tableData.length || loading
          }
          endIcon={<SaveAlt />}
          onClick={getReport}
        >
          {intl.formatMessage({ id: 'BUTTON.LABEL.DOWNLOAD_AS_XLSX' })}
        </ContainedButton>
      );
    }
    return (
      <ContainedButton
        id="downloadCsvButton"
        disabled={!tableData.length}
        endIcon={<SaveAlt />}
        onClick={getReport}
      >
        {intl.formatMessage({ id: 'BUTTON.LABEL.DOWNLOAD_AS_CSV' })}
      </ContainedButton>
    );
  };

  const controllerRef = useRef<AbortController>();

  const fetchReportsList = async () => {
    controllerRef.current?.abort();
    controllerRef.current = new AbortController();
    if (viewMode === 2) {
      // ENOVA tab doesn't show a table; skip listing
      setTableData([]);
      setTableDataEmpty(true);
      return;
    }
    const reportsResponse = await getFetch({
      url: fetchingUrlsObject.reports,
      setLoading,
      signal: controllerRef.current?.signal,
      queryParams: returnQueryParams(),
    });

    if (reportsResponse) {
      const { totalElements } = reportsResponse;

      if (pagination.totalElements !== totalElements) {
        setElementsCount(totalElements);
      }
      const dataWithFormattedMonths = formatMonthsNumbersToStringNames(
        reportsResponse.content,
      );
      setTableData(dataWithFormattedMonths);
      if (!dataWithFormattedMonths?.length) {
        setTableDataEmpty(true);
      }
    }
  };

  const setFilterDropdownData = useCallback(() => {
    setFilteredProjectsData(
      filterDropdownProjectData({
        projectsData,
        clientNameFromClient: client?.clientName,
      }),
    );
    setClientsValue({
      setValue,
      projectNameFromProject: project?.projectName,
      clientNameFromClient: client?.clientName,
      clientNameFromProject: project?.client?.clientName,
      clientIdFromProject: project?.client?.id,
    });
  }, [client, project, projectsData, setValue]);

  useEffect(() => {
    fetchReportsList();
    //	eslint-disable-next-line
  }, [
    viewMode,
    debouncedPage,
    pagination.pageSize,
    sorting.direction,
    sorting.column,
    currentLocale,
    dateFrom,
    project,
    dateTo,
    client,
    name,
    showMultiplicators,
    year,
    month,
    contractType,
  ]);

  useEffect(() => {
    const employeesFetch = async () => {
      const employeesResponse = await getFetch({ url: employeesUrl });
      setEmployeesData(employeesResponse.map(joinNames));
    };
    employeesFetch();
    fetchProjectList(setProjectsData);
    fetchClientList(setClientsData);
  }, []);

  const formatHeadData = useCallback(
    (viewMode: number) => {
      if (viewMode === 0) {
        return showMultiplicators
          ? TIMEREPORTS_HEAD_DATA
          : TIMEREPORTS_HEAD_DATA.filter(({ key }) => key !== 'multiplier');
      }
      if (viewMode === 1) {
        return BUSINESS_TRIPS_HEAD_DATA;
      }
      return [];
    },
    [showMultiplicators],
  );

  useEffect(() => {
    setTableHeadData(formatHeadData(viewMode));
  }, [viewMode, formatHeadData]);

  useEffect(() => {
    setFilterDropdownData();
  }, [setFilterDropdownData]);

  const toggleSorting = useCallback(
    (
      column: string,
      sorting: { column: string; direction: 'asc' | 'desc' },
      setSorting: Dispatch<any>,
    ) => {
      setSorting({
        column,
        direction: sorting.direction === 'asc' ? 'desc' : 'asc',
      });
    },
    [],
  );

  const clearAllFilters = useCallback(() => {
    setValue('project', { projectName: undefined });
    setValue('name', { value: null, name: null });
    setValue('client', { clientName: undefined });
    setValue('dateFrom', firstDay);
    setValue('dateTo', lastDay);
    setValue('year', { value: null, name: null });
    setValue('month', { value: null, name: null });
    setValue('contractType', { value: null, name: null });
  }, [firstDay, lastDay, setValue]);

  useEffect(() => {
    clearAllFilters();
    toggleSorting(
      'surname',
      { column: 'surname', direction: 'desc' },
      setSorting,
    );
  }, [clearAllFilters, toggleSorting, viewMode]);

  const isReportLoading =
    viewMode === 2 ? false : loading || (!tableData.length && !tableDataEmpty);

  return (
    <>
      <MenuBar value={viewMode} setValue={setViewMode} options={TAB_LABELS} />
      <Box overflow="auto" padding="2rem" minHeight="calc(100vh - 200px)">
        <form>
          <GridWrapperWithMargin container spacing={2}>
            <Grid item xs={12}>
              <Grid container spacing={2} justify="space-between">
                {(viewMode === 2 ? ENOVA_FILTER_DATA : REPORTS_FILTER_DATA).map(
                  (
                    entry: ReportsFilter,
                    entryIndex: number,
                    entryArray: ReportsFilter[],
                  ): JSX.Element => (
                    <Grid
                      lg={
                        entryIndex === entryArray.length - 1 ||
                        entryIndex === entryArray.length - 2
                          ? 6
                          : 4
                      }
                      sm={entryIndex === 0 ? 12 : 6}
                      key={entry.type}
                      xl={true}
                      xs={12}
                      item
                    >
                      <Controller
                        name={entry.type}
                        control={control}
                        render={({ onChange, value, name }) => {
                          const handleChange = (
                            e: ChangeEvent<HTMLInputElement>,
                          ) => {
                            onChange(e);
                            if (entry.handleChange) {
                              entry.handleChange();
                            }
                          };
                          return entry.component ===
                            FilterComponent.selectFilter ? (
                            <TimesheetFilter
                              onChange={handleChange}
                              value={value}
                              label={intl.formatMessage({ id: entry.label })}
                              type={name}
                              options={entry.options ?? []}
                            />
                          ) : (
                            <Datepicker
                              value={value}
                              label={intl.formatMessage({ id: entry.label })}
                              type={name}
                              name={entry.type}
                              maxDate={
                                entry.type === Filtering.dateFrom && dateTo
                                  ? moment(dateTo)
                                  : undefined
                              }
                              minDate={
                                entry.type === Filtering.dateTo && dateFrom
                                  ? moment(dateFrom)
                                  : undefined
                              }
                              onChange={onChange}
                              onClearButtonClick={() => {
                                setValue(entry.type, null);
                              }}
                            />
                          );
                        }}
                      />
                    </Grid>
                  ),
                )}
              </Grid>
            </Grid>
          </GridWrapperWithMargin>
        </form>
        <GridWrapperWithMargin2
          xs={12}
          justifyContent="space-between"
          container
          placement={'top'}
        >
          {viewMode === 0 && (
            <SwitchContainer
              name="switch-multipliers"
              id="switch-multipliers"
              label={intl.formatMessage({ id: 'TABLE.SWITCH.MULTIPLIERS' })}
              toggleSwitch={showMultiplicators}
              setToggleSwitch={handleSwitchChange}
            />
          )}
          <Grid item justifyContent="flex-end">
            {renderDownloadButton()}
          </Grid>
        </GridWrapperWithMargin2>
        {viewMode !== 2 && (
          <Table
            setCurrentPage={setCurrentPage}
            setRowsPerPage={setRowsPerPage}
            head={tableData.length ? tableHeadData : []}
            pageCount={pagination.totalElements}
            currentPage={pagination.pageNumber}
            withPagination={true}
            limit={pagination.pageSize}
            setSorting={setSorting}
            sorting={sorting}
            loading={isReportLoading}
            data={tableData}
            setShowMultiplicators={setShowMultiplicators}
            toggleWorktimeDisplay={toggleWorktime}
            toggleSorting={toggleSorting}
          />
        )}
      </Box>
    </>
  );
};

export default Reports;
